/**
 * AppRouter - Enrutador principal de la aplicación
 * 
 * OPTIMIZACIÓN: Code Splitting con React.lazy y Suspense
 * - Todas las páginas se cargan bajo demanda (lazy loading)
 * - Reduce significativamente el bundle inicial
 * - Mejora métricas FCP (First Contentful Paint) y TTI (Time to Interactive)
 */

import React, { useEffect, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from "react-router-dom";
import ReactGA from "react-ga";
import { GA_TRACKING_ID, BASE_PATHS } from "./constants";
import {
  homeRoute,
  institutionalRoutes,
  serviceRoutes,
  productRoutes,
  awardsRoutes,
  comercialLandingRoutes,
  pocLandingRoutes,
  specialLandingRoutes,
  webinarRoutes,
  notFoundRoute,
  VerticalGenericPage,
  SolutionsPage,
} from "./routeConfig";
import { LoadingFallback } from "../components/commons/LoadingFallback";
// Lazy loading para componentes importados directamente en AppRouter
const AdopcionIA = React.lazy(() => import("../pages/AdopcionIA"));
const IntegrationIA = React.lazy(() => import("../pages/IntegrationIA"));
const ProgramsIA = React.lazy(() => import("../pages/ProgramsIA").then(module => ({ default: module.ProgramsIA })));
const BancaIA = React.lazy(() => import("../pages/BancaIA").then(module => ({ default: module.BancaIA })));
const Agents = React.lazy(() => import("../pages/Agents").then(module => ({ default: module.Agents })));
const Data = React.lazy(() => import("../pages/Data").then(module => ({ default: module.Data })));
const Security = React.lazy(() => import("../pages/Security").then(module => ({ default: module.Security })));
const MicrosoftEcosystemLandingNew = React.lazy(() => import("../pages/MicrosoftEcosystemLanding").then(module => ({ default: module.MicrosoftEcosystemLanding })));
const GeneralIA = React.lazy(() => import("../pages/GeneralIA").then(module => ({ default: module.GeneralIA })));
const OilGasIA = React.lazy(() => import("../pages/OilGasIA").then(module => ({ default: module.OilGasIA })));
/**
 * Renderiza una ruta con su componente y provider opcional
 * Compatible con React.lazy - los componentes se cargan bajo demanda
 */
const renderRoute = (route, index) => {
  const { path, component: Component, provider: Provider, exact = true } = route;

  if (Provider) {
    return (
      <Route
        key={path || index}
        exact={exact}
        path={path}
        component={() => (
          <Suspense fallback={<LoadingFallback />}>
            <Provider>
              <Component />
            </Provider>
          </Suspense>
        )}
      />
    );
  }

  return (
    <Route
      key={path || index}
      exact={exact}
      path={path}
      component={() => (
        <Suspense fallback={<LoadingFallback />}>
          <Component />
        </Suspense>
      )}
    />
  );
};

export const AppRouter = () => {
  useEffect(() => {
    ReactGA.initialize(GA_TRACKING_ID);
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, []);

  return (
    <Router>
      {/* 
        Suspense envuelve todas las rutas para manejar el lazy loading
        LoadingFallback se muestra mientras se carga el chunk de la página
      */}
      <Suspense fallback={<LoadingFallback />}>
        <Switch>
          {/* RUTA PRINCIPAL - HOME */}
          {renderRoute(homeRoute)}

          {/* SERVICIOS PRINCIPALES */}
          {/* Nota: Dedicated Teams primero por especificidad de ruta */}
          {renderRoute(serviceRoutes[0])} {/* DedicatedTeams */}
          {renderRoute(serviceRoutes[1])} {/* DevOpsSre */}
          {renderRoute(serviceRoutes[2])} {/* SoftwareServices */}
          {renderRoute(serviceRoutes[3])} {/* StaffAugmentation */}
          {renderRoute(serviceRoutes[4])} {/* MicrosoftEcosystem */}
          {renderRoute(serviceRoutes[5])} {/* Itaas */}

          {/* PÁGINAS INSTITUCIONALES */}
          {institutionalRoutes.map((route, i) => renderRoute(route, `inst-${i}`))}

          {/* PRODUCTOS Y POLÍTICAS */}
          {renderRoute(productRoutes[2])} {/* OrganizationalPolicy */}
          {renderRoute(productRoutes[0])} {/* Products */}
          {renderRoute(productRoutes[1])} {/* Products con sección */}

          {/* AWARDS Y RECONOCIMIENTOS */}
          {awardsRoutes.map((route, i) => renderRoute(route, `awards-${i}`))}

          {/* LANDINGS COMERCIALES */}
          {comercialLandingRoutes.map((route, i) => renderRoute(route, `comercial-${i}`))}

          {/* SOLUCIONES DINÁMICAS (NUEVO SISTEMA) */}
          {/* Redirects: Eliminar prefijos bilingües de URLs de soluciones */}
          <Route exact path="/en-en/solutions/:slug">
            {({ match }) => <Redirect to={`/solutions/${match.params.slug}`} />}
          </Route>
          <Route exact path="/es-es/soluciones/:slug">
            {({ match }) => <Redirect to={`/soluciones/${match.params.slug}`} />}
          </Route>
          <Route exact path="/en-en/solutions">
            <Redirect to="/solutions" />
          </Route>
          <Route exact path="/es-es/soluciones">
            <Redirect to="/soluciones" />
          </Route>

          {/* Redirects para Microsoft Ecosystem Landing (URLs antiguas) */}
          <Route exact path="/es-es/microsoft-ecosystem">
            <Redirect to="/es/microsoft-ecosystem" />
          </Route>
          <Route exact path="/en-en/microsoft-ecosystem">
            <Redirect to="/en/microsoft-ecosystem" />
          </Route>

          {/* Ruta específica para Adopción IA (acepta ambos slugs) */}
          <Route exact path={BASE_PATHS.SOLUTIONS_ADOPCION_IA}>
            {() => (
              <Suspense fallback={<LoadingFallback />}>
                <AdopcionIA />
              </Suspense>
            )}
          </Route>
          <Route exact path={BASE_PATHS.SOLUTIONS_INTEGRATION_IA}>
            {() => (
              <Suspense fallback={<LoadingFallback />}>
                <IntegrationIA />
              </Suspense>
            )}
          </Route>
          <Route exact path={BASE_PATHS.SOLUTIONS_AGENTS}>
            {() => (
              <Suspense fallback={<LoadingFallback />}>
                <Agents />
              </Suspense>
            )}
          </Route>
          {/* Ruta estática para Data Science */}
          <Route exact path={BASE_PATHS.SOLUTIONS_DATA}>
            {() => (
              <Suspense fallback={<LoadingFallback />}>
                <Data />
              </Suspense>
            )}
          </Route>
          {/* Ruta estática para Security */}
          <Route exact path={BASE_PATHS.SOLUTIONS_SECURITY}>
            {() => (
              <Suspense fallback={<LoadingFallback />}>
                <Security />
              </Suspense>
            )}
          </Route>
          {/* Ruta estática para Microsoft Ecosystem Landing */}
          <Route exact path={BASE_PATHS.MICROSOFT_ECOSYSTEM_LANDING}>
            {() => (
              <Suspense fallback={<LoadingFallback />}>
                <MicrosoftEcosystemLandingNew />
              </Suspense>
            )}
          </Route>
          <Route exact path={BASE_PATHS.SOLUTIONS_INDEX}>
            {() => (
              <Suspense fallback={<LoadingFallback />}>
                <SolutionsPage />
              </Suspense>
            )}
          </Route>
          <Route path={BASE_PATHS.SOLUTIONS_DETAIL}>
            {() => (
              <Suspense fallback={<LoadingFallback />}>
                <VerticalGenericPage />
              </Suspense>
            )}
          </Route>

          {/* LANDINGS COMERCIALES ESTATICAS */}
          <Route path={BASE_PATHS.LANDINGS_COMERCIAL} component={ProgramsIA} />
          <Route path={BASE_PATHS.LANDINGS_COMERCIAL_BANCA} component={BancaIA} />
          <Route path={BASE_PATHS.LANDINGS_COMERCIAL_GENERAL} component={GeneralIA} />
          <Route path={BASE_PATHS.LANDINGS_COMERCIAL_OIL_GAS} component={OilGasIA} />

          {/* LANDINGS POC GENÉRICAS */}
          {pocLandingRoutes.map((route, i) => renderRoute(route, `poc-${i}`))}


          {/* LANDINGS ESPECIALES */}
          {/* AI Software Solutions */}
          {renderRoute(specialLandingRoutes[0])}
          {/* Nearshore */}
          {renderRoute(specialLandingRoutes[1])}
          {renderRoute(specialLandingRoutes[2])}
          {/* Redirect Houston a Nearshore */}
          <Route exact path={BASE_PATHS.HOUSTON_REDIRECT}>
            <Redirect to="/nearshore-latam-us" />
          </Route>
          {/* Simple Pay */}
          {renderRoute(specialLandingRoutes[3])}

          {/* WEBINARS */}
          {webinarRoutes.map((route, i) => renderRoute(route, `webinar-${i}`))}

          {/* 404 - DEBE SER ÚLTIMA RUTA */}
          {renderRoute(notFoundRoute)}
        </Switch>
      </Suspense>
    </Router>
  );
};

export default AppRouter;
